import api from './api';
import type { BlisterAckSide, BlisterChangeAcknowledgements } from './patientBlisterChangeService';

/** The blister settings as the API serves them. Every date is a plain string. */
export interface PatientBlisterSettings {
  blisterPack: boolean;
  cycleDays: number | null;
  packSize: 'large' | 'small' | null;
  source: 'fillware' | 'manual' | null;
  nextPackDate: string | null;
  lastPackSentAt: string | null;
  lastPackSentDate: string | null;
  updatedBy: string | null;
  updatedAt: string | null;
  /** False at a Kroll/GreenShield store, where the reversal can only be a 400. */
  canFollowFillware: boolean;
  /**
   * PRX-48: the pack changed and nobody has verified the change, so no pack can
   * be sent. The header chip and the signoff banner read this ONE payload.
   */
  changePending: boolean;
  /** Why, already worded for a human. Empty whenever nothing is pending. */
  changeReasons: BlisterChangeReason[];
  /**
   * PRX-66: the two questions have been answered and the pack is waiting only
   * for a `verifyBlisterChanges` sign-off. False whenever nothing is pending.
   */
  changeAnswered: boolean;
  /**
   * PRX-105: who has acknowledged the pending change, by name, per side. Both
   * null whenever nothing is pending.
   */
  changeAcknowledgements: BlisterChangeAcknowledgements;
  /**
   * PRX-105: the sides still to acknowledge, Billing first. The sign-off is
   * refused until this is empty.
   */
  changeMissingAcknowledgements: BlisterAckSide[];
  /**
   * WP-17: the sides the pending change needs, Billing first. An alongside
   * change needs Billing only. Read through `requiredBlisterAckSides`.
   */
  changeRequiredAcknowledgements?: BlisterAckSide[];
  /**
   * PRX-102: the blister dates, computed by the SERVER from the next pack date
   * on every read (`computeBlisterProtocol`). The client never recomputes them.
   */
  protocol: BlisterProtocol;
  /**
   * PRX-127: whether the pack size follows from the slot chart or stands as
   * staff set it. Absent on an older server, which the UI reads as unknown and
   * shows nothing derived.
   */
  packSizeDerivation?: BlisterPackSizeDerivation;
  /** PRX-127: blister packaging consent. Absent or unrecorded reads as not on file. */
  consent?: BlisterConsent;
  /**
   * WP-14 (PRX-141 Q5): the ODB frequent-dispensing exemption. Null at a
   * pharmacy that is not on Fillware, or when it could not be read; absent on an
   * older server.
   */
  odbExemption?: BlisterOdbExemption | null;
}

/** PRX-127: an in-pack drug with no slots entered. */
export interface BlisterSlotGap {
  rxNumber: string;
  medicationName: string;
}

/**
 * PRX-127: `slots` means every in-pack drug has slots and the size follows from
 * them (any noon or supper units make it large). `manual` means the stored size
 * stands as staff set it; `missingSlots` names the drugs that lack slots.
 */
export type BlisterPackSizeDerivation =
  | { mode: 'slots'; packSize: 'large' | 'small'; missingSlots: [] }
  | {
    mode: 'manual';
    packSize: null;
    reason: 'no_in_pack_drugs' | 'slots_missing';
    missingSlots: BlisterSlotGap[];
  };

/** PRX-127: blister packaging consent, as recorded. */
export interface BlisterConsent {
  onFile: boolean;
  /** False until anyone records an answer. */
  recorded: boolean;
  /** YYYY-MM-DD, the day it was signed. */
  consentDate: string | null;
  recordedBy: string | null;
  recordedAt: string | null;
}

/** WP-14 (PRX-141 Q5): the ODB frequent-dispensing exemption, as recorded. */
export interface BlisterOdbExemption {
  /** YYYY-MM-DD, the day it was signed; null when none is on file. */
  exemptionDate: string | null;
  /** A year on, computed by the server. */
  dueDate: string | null;
  /** Computed by the server on the pharmacy day. Null with no date. */
  state: 'valid' | 'due_soon' | 'overdue' | null;
  recordedBy: string | null;
  recordedAt: string | null;
}

/** PRX-127: what the consent route accepts. `consentDate` only with `onFile: true`. */
export interface UpdateBlisterConsentPayload {
  onFile: boolean;
  consentDate?: string | null;
}

/**
 * An input the server needed and did not have, so the fields that use it are
 * null. The anchor has three states: unset (`cycleAnchor`), the settings read
 * failed (`cycleAnchorUnavailable`) and not a Monday (`cycleAnchorInvalid`).
 * Mirrors `BlisterProtocolMissing` in `blister-dates.util.ts`.
 */
export type BlisterProtocolMissing =
  | 'nextPackDate'
  | 'packSize'
  | 'frequency'
  | 'cycleAnchor'
  | 'cycleAnchorUnavailable'
  | 'cycleAnchorInvalid';

/** PRX-102. Every date is a plain YYYY-MM-DD string; null means not set. */
export interface BlisterProtocol {
  dueDate: string | null;
  processingDate: string | null;
  processingWeekday: string | null;
  billingDueDate: string | null;
  /** The processing date has passed, so the pack is billed today. */
  billNow: boolean | null;
  nextBillingDate: string | null;
  frequencyWeeks: 2 | 4 | 12 | null;
  cycleAnchor: string | null;
  todayCycle: number | null;
  dueCycle: number | null;
  processingCycle: number | null;
  /** '1', '2', '3', '4S' or '4L'. */
  synGroup: string | null;
  /** '1 & 3' / '2 & 4' for a 2-week patient, else the one cycle. */
  patientBillingCycle: { label: string; cycles: number[] } | null;
  synDueDate: string | null;
  /** The SYN due date is before the start of this week's SynMed run. */
  synLate: boolean | null;
  missing: BlisterProtocolMissing[];
}

/** One human-worded reason a patient's pack changed. Display only. */
export interface BlisterChangeReason {
  type: string;
  rxNumber?: string;
  din?: string;
  drugName?: string;
  /** One sentence, already worded for a human. */
  detail: string;
  /** ISO timestamp. */
  detectedAt: string;
  /** User email, or 'system:fillware-sync'. */
  detectedBy: string;
}

/**
 * A manual edit, or the reversal.
 *
 * `followFillware: true` is mutually exclusive with the manual keys — the API
 * refuses the combination rather than picking a winner.
 */
export interface UpdatePatientBlisterPayload {
  blisterPack?: boolean;
  cycleDays?: number | null;
  packSize?: 'large' | 'small' | null;
  /** A plain YYYY-MM-DD day, or null to clear the schedule. Never a timestamp. */
  nextPackDate?: string | null;
  /** PRX-102: required by the server when a set next pack date moves or is cleared. */
  reason?: string;
  followFillware?: boolean;
}

/**
 * PRX-46 — the three pharmacy-scoped blister routes.
 *
 * The pharmacy is in the path on every one of them, and the server refuses a
 * patient belonging to another store with a 404. Nothing here falls back to a
 * default when a call fails: an error propagates to TanStack Query, and the
 * caller renders nothing rather than a plausible wrong schedule.
 */
const patientBlisterService = {
  async get(pharmacyId: string, patientId: string): Promise<PatientBlisterSettings> {
    const response = await api.get<PatientBlisterSettings>(
      `/api/patients/${pharmacyId}/${patientId}/blister`,
    );
    return response.data;
  },

  async update(
    pharmacyId: string,
    patientId: string,
    payload: UpdatePatientBlisterPayload,
  ): Promise<PatientBlisterSettings> {
    const response = await api.patch<PatientBlisterSettings>(
      `/api/patients/${pharmacyId}/${patientId}/blister`,
      payload,
    );
    return response.data;
  },

  /** PRX-127: record blister packaging consent. Who and when are stamped by the server. */
  async updateConsent(
    pharmacyId: string,
    patientId: string,
    payload: UpdateBlisterConsentPayload,
  ): Promise<PatientBlisterSettings> {
    const response = await api.patch<PatientBlisterSettings>(
      `/api/patients/${pharmacyId}/${patientId}/blister/consent`,
      payload,
    );
    return response.data;
  },

  /** WP-14 (PRX-141 Q5): record the ODB exemption date, or clear it with null. Who and when are stamped by the server. */
  async updateOdbExemption(
    pharmacyId: string,
    patientId: string,
    payload: { exemptionDate: string | null },
  ): Promise<PatientBlisterSettings> {
    const response = await api.patch<PatientBlisterSettings>(
      `/api/patients/${pharmacyId}/${patientId}/blister/odb-exemption`,
      payload,
    );
    return response.data;
  },

  async resync(pharmacyId: string, patientId: string): Promise<PatientBlisterSettings> {
    const response = await api.post<PatientBlisterSettings>(
      `/api/patients/${pharmacyId}/${patientId}/blister/resync`,
    );
    return response.data;
  },
};

export default patientBlisterService;
